import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { getSalesTeamList } from '@user/store/salesTeam/api'
import { ManageDefaultsDrawer } from './ManageDefaults/ManageDefaultsDrawer'
import { SalesTeamList } from './SalesTeamList'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { SettingsPage, SideDrawer } from '@sales-center/components'
import { updateLoadingState } from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useGetBranchListOnMount } from '@user/store/branches/hooks/useGetBranchListOnMount'
import { useGetRolesOnMount } from '@user/store/roles/hooks'
import {
  useGetSalesTeam,
  useGetSalesTeamListOnMount,
} from '@user/store/salesTeam/hooks'

import Button from '@mui/material/Button'
import { AddSalesTeamFormWrapper } from './forms/AddSalesTeamFormWrapper'

export function SalesTeams() {
  const dispatch = useAppDispatch()
  const [showEmployees, setShowEmployees] = useState<boolean>(false)
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false)

  const rolesStatus = useGetRolesOnMount()
  const teamStatus = useGetSalesTeamListOnMount({ checkUsersCount: true })
  const salesTeams = useSelector(selectAllSalesTeams)

  const branchStatus = useGetBranchListOnMount()

  const { status: editStatus, team, setTeamId } = useGetSalesTeam(showEmployees)

  const handleShowEmployeesChange = useCallback(async () => {
    setLoadingEmployees(true)
    await dispatch(
      getSalesTeamList({
        includeUsers: !showEmployees,
        includeInactiveUsers: !showEmployees,
        checkUsersCount: true,
      }),
    )
    setShowEmployees(!showEmployees)
    setLoadingEmployees(false)
  }, [dispatch, showEmployees])

  const handleRefreshList = useCallback(async () => {
    setLoadingEmployees(true)
    await dispatch(
      getSalesTeamList({
        includeUsers: showEmployees,
        includeInactiveUsers: showEmployees,
        checkUsersCount: true,
      }),
    )
    setLoadingEmployees(false)
  }, [dispatch, showEmployees])

  //#region add team form
  const [addTeamFormOpen, setAddTeamFormOpen] = useState(false)

  const showAddTeamForm = useCallback(() => setAddTeamFormOpen(true), [])

  const hideAddTeamForm = useCallback(() => {
    setAddTeamFormOpen(false)
    setTeamId(undefined)
  }, [setTeamId])
  //#endregion

  const [manageDefaultsOpen, setManageDefaultsOpen] = useState(false)
  const openManageDefaults = useCallback(() => setManageDefaultsOpen(true), [])
  const closeManageDefaults = useCallback(() => {
    setManageDefaultsOpen(false)
    handleRefreshList()
  }, [handleRefreshList])

  const onEditClick = useCallback((id: number) => setTeamId(id), [setTeamId])

  // show loading state
  useEffect(() => {
    const status =
      loadingEmployees ||
      teamStatus == 'pending' ||
      branchStatus == 'pending' ||
      editStatus == 'pending' ||
      rolesStatus == 'pending'

    dispatch(updateLoadingState(status ? 'visible' : 'hidden'))
  }, [
    branchStatus,
    dispatch,
    editStatus,
    loadingEmployees,
    teamStatus,
    rolesStatus,
  ])

  useEffect(() => {
    if (editStatus == 'success' && !!team) {
      setAddTeamFormOpen(true)
    }
  }, [editStatus, team])
  const theme = useTheme()
  return (
    <SettingsPage title="Sales Teams">
      <Button
        color="primary"
        variant="contained"
        onClick={showAddTeamForm}
        disableElevation
      >
        Add Sales Team
      </Button>
      <Button
        color="primary"
        disableElevation
        onClick={openManageDefaults}
        style={{ marginLeft: theme.spacing(2) }}
        variant="contained"
      >
        Manage Default Sales Teams
      </Button>
      <SideDrawer
        title={`${team ? 'Edit' : 'Add'} Sales Team`}
        open={addTeamFormOpen}
        onClose={hideAddTeamForm}
      >
        <AddSalesTeamFormWrapper editTeam={team} onClose={hideAddTeamForm} />
      </SideDrawer>
      <SalesTeamList
        handleShowEmployeesChange={handleShowEmployeesChange}
        onEdit={onEditClick}
        salesTeams={salesTeams}
        showEmployees={showEmployees}
        handleRefreshList={handleRefreshList}
      />
      <ManageDefaultsDrawer
        onClose={closeManageDefaults}
        open={manageDefaultsOpen}
      />
    </SettingsPage>
  )
}
